<template>
  <div
    class="audio-player"
    :class="{
      'audio-player--mobile': isMobile,
      'audio-player--pc-detail': isPcDetail,
      'audio-player--inxbb-quick-view': inxbbQuickView
    }"
  >
    <div class="audio-player__audio">
      <audio
        ref="audioRef"
        :src="audioUrl"
        @ended="handleEnded"
        @loadedmetadata="handleDurationChange"
        @timeupdate="handleTimeupdate"
      ></audio>
    </div>
    <div class="audio-player__info">
      <div class="audio-player__content">
        <i v-if="isQuick" class="btn icon-replay-15-line t-iconfont" @click="handleReplay"></i>
        <div v-if="audioStatus === 'pause'" class="audio-player__play">
          <i class="icon-play-circle-fill t-iconfont" @click.stop="handlePlay"></i>
        </div>
        <div v-else class="audio-player__play">
          <i class="icon-pause-circle-fill t-iconfont" @click.stop="handlePlay"></i>
        </div>
        <i v-if="isQuick" class="btn icon-forward-15-line t-iconfont" @click="handleForward"></i>
        <template v-if="!isMobile">
          <el-slider
            v-model="currentTimeVale"
            :format-tooltip="formatTooltip"
            :max="numberDuration"
            @change="handleChangeTime"
            @input="handleInputTime"
          />
          <span class="audio-player__time">{{ currentTime }}/{{ fileDuration }}</span>
        </template>
        <div v-else class="audio-player__bar">
          <el-slider
            v-model="currentTimeVale"
            :max="numberDuration"
            :show-tooltip="false"
            @change="handleChangeTime"
            @input="handleInputTime"
          />
          <div class="time">
            <span class="audio-player__time">{{ currentTime }}</span>
            <span class="audio-player__time">{{ fileDuration }}</span>
          </div>
        </div>
        <template v-if="showDownload">
          <el-tooltip
            v-if="isPcDetail"
            class="box-item"
            content="下载音频"
            effect="dark"
            placement="top"
          >
            <div class="audio-player__download audio-player__download--pc" @click="handleDownload">
              <i class="icon-download-2-line t-iconfont"></i>
            </div>
          </el-tooltip>
          <div
            v-else-if="isMobile"
            class="audio-player__download audio-player__download--mobile"
            @click="handleDownload"
          >
            <i class="icon-download-2-line t-iconfont"></i>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, defineEmits } from 'vue'
import utils from '@/utils'
import dayjs from 'dayjs'

const emit = defineEmits(['play', 'pause'])
import xbbSdk from 'xbb-sdk'
import { showToast } from 'vant'

const props = defineProps({
  audioUrl: {
    type: String,
    default: ''
  },
  isMobile: {
    type: Boolean,
    default: false
  },
  isPcDetail: {
    type: Boolean,
    default: false
  },
  showDownload: {
    type: Boolean,
    default: false
  },
  inxbbQuickView: {
    type: Boolean,
    default: false
  },
  isQuick: {
    type: Boolean,
    default: true
  }
})

const audioRef = ref(null)
const fileDuration = ref('00:00:00')
const currentTime = ref('00:00:00')

const currentTimeVale = ref(0)
const numberDuration = ref(0)
const isDraging = ref(false)

const audioStatus = ref('pause')

// audioUrl变化的时候重置状态
watch(
  () => props.audioUrl,
  () => {
    fileDuration.value = '00:00:00'
    currentTime.value = '00:00:00'
    currentTimeVale.value = 0
    numberDuration.value = 0
    isDraging.value = false
    audioStatus.value = 'pause'
  }
)

const handleStartWithTime = (time) => {
  currentTimeVale.value = time
  handleChangeTime(currentTimeVale.value)
}

const handleReplay = () => {
  if (currentTimeVale.value < 15) {
    currentTimeVale.value = 0
  } else {
    currentTimeVale.value = currentTimeVale.value - 15
  }
  handleChangeTime(currentTimeVale.value)
}

const handleForward = () => {
  currentTimeVale.value = currentTimeVale.value + 15
  handleChangeTime(currentTimeVale.value)
}

const handleChangeTime = (value) => {
  console.log(value)
  audioRef.value.currentTime = value
  audioRef.value.play()
  audioStatus.value = 'play'
  isDraging.value = false
  emit('play')
}

const handleInputTime = (val) => {
  isDraging.value = true
}

const formatTooltip = (val) => {
  return formatDuration(val)
}

const formatDuration = (duration) => {
  if (!duration) return '00:00:00'
  const hours = Math.floor(duration / 3600)
  const minutes = Math.floor((duration % 3600) / 60)
  const seconds = Math.floor(duration % 60)
  return [hours, minutes, seconds].map((val) => val.toString().padStart(2, '0')).join(':')
}

const handleDurationChange = () => {
  console.log('handleDurationChange')
  fileDuration.value = formatDuration(audioRef.value.duration)
  numberDuration.value = audioRef.value.duration
}

function handleTimeupdate() {
  if (!audioRef.value) return
  if (!isDraging.value) {
    currentTimeVale.value = audioRef.value.currentTime
  }
  // const percent = (audioRef.value.currentTime / audioRef.value.duration) * 100
  currentTime.value = formatDuration(audioRef.value.currentTime)
}

function handleEnded() {
  audioStatus.value = 'pause'
  currentTimeVale.value = 0
  currentTime.value = '00:00:00'
}

function formatTime(timestamp) {
  const date = dayjs(timestamp * 1000)
  return date.format('YYYY-MM-DD')
}

const handlePlay = () => {
  if (audioRef.value.paused) {
    audioRef.value.play()
    audioStatus.value = 'play'
    emit('play')
  } else {
    audioRef.value.pause()
    audioStatus.value = 'pause'
    emit('pause')
  }
}

// 用于外部多列表音频播放，切换播放，暂停音频的播放
const handlePause = () => {
  audioRef.value.pause()
  audioStatus.value = 'pause'
  emit('pause')
}

const handleDownload = () => {
  if (props.isMobile) {
    // 复制到剪切板
    utils.copyToClipboard(
      props.audioUrl,
      () => {
        showToast('已复制下载链接')
      },
      (type) => {
        console.log('复制失败')
        showToast('复制失败', type)
      }
    )
  } else if (props.isPcDetail) {
    let { audioUrl } = props
    if (location.host === 'appinsidefront.xbongbong.com' && !audioUrl.startsWith('https://')) {
      audioUrl = audioUrl.replace(/^http:\/\//i, 'https://') //独立版url协议为http时，转换为https防止跨域
    }
    xbbSdk.sendMessage('xbbDownloadFile', {
      url: audioUrl,
      name: `${formatTime(props.addTime)}.mp3`
    })
  }
}

defineExpose({
  handlePause,
  handleStartWithTime,
  numberDuration
})
</script>

<style scoped lang="less">
.audio-player {
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  border-radius: 8px;
  background: #ffffff;
  position: relative;

  audio {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
  }

  .audio-player__info {
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    display: flex;
    align-items: center;

    .upload-progress {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    .audio-player__play {
      margin: 0 8px;
      cursor: pointer;
    }
    .audio-player__time {
      font-family: PingFang SC;
      font-size: 14px;
      line-height: 20px;
      color: #646566;
      margin-left: 8px;
    }

    .audio-player__container {
      position: absolute;
      padding: 2px 4px;
      background-color: rgba(0, 0, 0, 0.5);
      color: #fff;
      border-radius: 4px;
      font-size: 12px;
    }

    .audio-player__info-type {
      top: 8px;
      right: 8px;
      background-color: rgba(0, 0, 0, 0.5);
      color: #fff;
    }

    .audio-player__info-size {
      bottom: 8px;
      left: 8px;
    }

    .audio-player__info-duration {
      bottom: 8px;
      right: 8px;
    }

    .audio-player__content {
      display: flex;
      align-items: center;
      width: 100%;
      height: 100%;
      .el-slider {
        margin-left: 12px;
      }
      :deep(.el-slider__bar) {
        background-color: #7a52f4;
      }
      :deep(.el-slider__button) {
        height: 10px;
        width: 10px;
        border: 2px solid #7a52f4;
      }
      :deep(.el-slider__button-wrapper) {
        height: 20px;
        width: 20px;
        top: -8px;
      }
      .btn {
        color: #969799;
        font-size: 24px;
        cursor: pointer;
      }
      .audio-player__play {
        .t-iconfont {
          font-size: 40px;
          background: linear-gradient(315deg, #1574ff 2%, #5952ff 51%, #a969fe 96%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          text-fill-color: transparent;
        }
      }
      .audio-player__bar {
        flex: 1;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        position: relative;
        height: 100%;
        justify-content: end;
        :deep(.el-slider) {
          width: calc(100% - 12px);
          height: 16px;
        }
        .time {
          line-height: 16px;
          font-size: 12px;
          color: #646566;
          display: flex;
          justify-content: space-between;
        }
      }
      .audio-player__download {
        width: 28px;
        height: 28px;
        border-radius: 8px;
        cursor: pointer;
        flex: 0 0 auto;
        display: flex;
        justify-content: center;
        align-items: center;
        margin-left: 12px;
      }
      .audio-player__download--pc:hover {
        background: #e5e6eb;
      }
      .audio-player__download--mobile {
        // background: #e5e6eb;
        color: #323233;
        font-size: 14px;
      }
    }
  }
}
.audio-player--mobile {
  background: #f2f3f5;
  .btn {
    font-size: 20px !important;
  }
}
.audio-player--pc-detail {
  border-radius: 0px;
  background: #f2f3f5;
}
.audio-player--inxbb-quick-view {
  // 稍微加一个安全底部
  padding: 10px;
}
</style>
